/**
 * Black Box — core tracer. Captures the event taxonomy in schema.js.
 * Every listener is wrapped to fail silent; the tracer must never break
 * the host checkout page or capture any input value.
 */
import { EVENTS, STEPS, scrubText, goalName } from './schema.js';
import { elId, clickTarget, matchField, isRendered } from './util.js';
import { snapshotDom } from './dom.js';

export function createTracer(config, session, transport) {
  const t0 = Date.now();
  const now = () => Date.now() - t0;

  let currentStep = STEPS.LANDING;
  let lastActivity = t0;
  let maxScroll = 0;
  let converted = false;
  let ended = false;
  let endReason = null;   // why the session ended — idle_timeout ends are resurrectable

  const focusStart = {};       // elId -> ts
  const fieldHadContent = {};  // field -> bool (for retype detection)
  const retypes = {};          // field -> count
  let otpAttempts = 0;

  // rapid same-spot click tracking
  let rage = { id: null, count: 0, start: 0 };
  let lastSubmit = { id: null, ts: 0, count: 0 };

  let emitted = 0;
  let rateWinStart = Date.now(), rateCount = 0;
  const maxEvents = config.maxEvents || 5000;
  const rateMax = config.rateMaxPerSec || 80; // circuit breaker: drop bursts beyond this/sec
  function emit(e, payload) {
    if (ended && e !== EVENTS.SESSION_END) return;
    // Hard safety cap: never let a runaway page flood the browser or collector.
    // session_end always allowed through so the session still finalizes.
    if (emitted >= maxEvents && e !== EVENTS.SESSION_END) {
      if (emitted === maxEvents) { emitted++; try { end('event_cap'); } catch (_) {} }
      return;
    }
    // Per-second circuit breaker: silently DROP anything past the rate ceiling so
    // an event storm can't pin the CPU or flood the collector — the page stays alive.
    if (e !== EVENTS.SESSION_END) {
      const ms = Date.now();
      if (ms - rateWinStart >= 1000) { rateWinStart = ms; rateCount = 0; }
      if (++rateCount > rateMax) return;
    }
    emitted++;
    try {
      transport.push(Object.assign({ t: now(), e }, payload || {}));
    } catch (_) {}
  }

  // Coalesce rapid callbacks into one per animation frame, and fail silent — used
  // to keep MutationObservers from triggering a reflow storm on busy pages.
  const raf = window.requestAnimationFrame
    ? function (cb) { window.requestAnimationFrame(cb); }
    : function (cb) { setTimeout(cb, 16); };
  function rafDebounce(fn) {
    let scheduled = false;
    return function () {
      if (scheduled) return;
      scheduled = true;
      raf(function () { scheduled = false; try { fn(); } catch (_) {} });
    };
  }

  let idleEmitted = false;
  function activity() {
    lastActivity = Date.now();
    idleEmitted = false;
    // Resurrect a session that was finalized by the idle timeout (NOT by a real
    // unload/conversion): the user came back — e.g. a slow SMS — so keep recording.
    // The next session_end re-finalizes server-side with the true outcome, so a
    // late conversion is never miscounted as a drop.
    if (ended && endReason === 'idle_timeout') {
      ended = false;
      endReason = null;
      try { transport.start(); } catch (_) {}
      if (!idleTimer) idleTimer = setInterval(idleTick, 2000);
    }
  }

  // Selectors captured in the one-time DOM snapshot — lets a click flag whether it
  // hit a known page control or an element injected later (antifraud/ads/bots).
  let snapshotSels = null;

  // ---- page_load (first event) ----
  function emitPageLoad() {
    const c = session.context;
    emit(EVENTS.PAGE_LOAD, {
      device: c.device, browser: c.browser, os: c.os,
      viewport: c.viewport, screen: c.screen,
      market: c.market, language: c.language,
      path: c.path, referrer: c.referrer, connection: c.connection,
    });
  }

  // djb2 hash — tiny, deterministic; good enough to tell page versions apart.
  function fpHash(s) {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    return h.toString(36);
  }

  // ---- one-time DOM structure snapshot (identity + semantics, NO values) ----
  function emitDomSnapshot() {
    try {
      const els = snapshotDom(config);
      if (els && els.length) {
        snapshotSels = new Set(els.map((e) => e.sel));
        emit(EVENTS.DOM_SNAPSHOT, { els });
        // Page-version fallback (Deployments benchmark): when the tag declares no
        // data-bb-version, fingerprint the STABLE form skeleton — selector, tag,
        // type, step — of the snapshotted controls. Injected third-party elements
        // aren't in the snapshot (form controls only), so antifraud noise doesn't
        // churn the version. Set before the first flush; sent with the context.
        if (!session.context.page_version) {
          const skeleton = els.map((e) => [e.sel, e.tag, e.type || '', e.step || ''].join(':')).sort().join('|');
          session.context.page_version = 'fp-' + fpHash(skeleton);
        }
      }
      if (config.debug) {
        try {
          console.log('[BlackBox] DOM snapshot —', (els && els.length) || 0, 'elements captured');
          if (console.table) console.table(els); else console.log(els);
        } catch (_) {}
      }
    } catch (_) {}
  }

  // Cheap, value-free facts about a clicked element: what it is, its size/visibility,
  // its index among identical-selector siblings (so replay clicks the right one), and
  // whether it was a control we snapshotted at load vs injected later. NO values/PII.
  function clickMeta(el, sel) {
    const m = {};
    try {
      m.tag = (el.tagName || '').toLowerCase();
      let r = null; try { r = el.getBoundingClientRect(); } catch (_) {}
      // element size as ew/eh — NOT w/h, which would clobber the viewport width (w)
      // the click already carries (the heatmap normalises x against the viewport).
      if (r) { m.ew = Math.round(r.width); m.eh = Math.round(r.height); }
      m.vis = !!(isRendered(el) && r && (r.width > 0 || r.height > 0));
      try {
        const all = document.querySelectorAll(sel);
        if (all.length > 1) m.nth = Array.prototype.indexOf.call(all, el); // disambiguate siblings (e.g. FAQ items)
      } catch (_) {}
      if (snapshotSels) m.known = snapshotSels.has(sel);
    } catch (_) {}
    return m;
  }

  // ---- clicks + rage + repeated submit ----
  function onClick(ev) {
    activity();
    // Resolve a tap on an inner span/icon to its button/link so the selector is
    // stable and replay clicks the right node (see clickTarget).
    const el = clickTarget(ev.target);
    const id = elId(el);
    const meta = clickMeta(el, id);
    // page-relative tap position (document px) + viewport width + funnel step — where
    // on the page they clicked, for the heatmap. Not PII.
    const pos = { x: Math.round(ev.pageX || 0), y: Math.round(ev.pageY || 0), w: window.innerWidth || 0, step: currentStep };
    emit(EVENTS.CLICK, Object.assign({ el: id }, pos, meta));

    // A click on a disabled control never fires on the control itself — the event
    // targets an ancestor. Hit-test disabled controls under the tap so "clicked
    // the disabled CTA" (e.g. wrong-format phone number) is measurable.
    try {
      if (ev.target && ev.target.querySelectorAll) {
        const dis = ev.target.querySelectorAll('button:disabled,input:disabled');
        for (let i = 0; i < dis.length; i++) {
          const r = dis[i].getBoundingClientRect();
          if (ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom) {
            emit(EVENTS.DISABLED_CLICK, Object.assign({ el: elId(dis[i]) }, pos));
            break;
          }
        }
      }
    } catch (_) {}

    const ts = Date.now();
    if (rage.id === id && ts - rage.start <= config.rageWindowMs) {
      rage.count++;
      if (rage.count >= config.rageThreshold) emit(EVENTS.RAGE_CLICK, Object.assign({ el: id, count: rage.count }, pos, meta));
    } else {
      rage = { id, count: 1, start: ts };
    }

    // repeated submit: clicking a submit-like control again within 2s
    try {
      const isSubmit = (el.type === 'submit') || /submit|confirm|activate/i.test(id) ||
        (el.tagName === 'BUTTON');
      if (isSubmit) {
        if (lastSubmit.id === id && ts - lastSubmit.ts < 2000) {
          lastSubmit.count++;
          emit(EVENTS.REPEATED_SUBMIT, { el: id, count: lastSubmit.count });
        } else {
          lastSubmit = { id, ts, count: 1 };
        }
        if (currentStep === STEPS.OTP) {
          otpAttempts++;
          emit(EVENTS.OTP_ATTEMPT, { n: otpAttempts, result: 'submit' });
        }
      }
    } catch (_) {}
  }

  // ---- focus / blur with dwell ----
  function onFocus(ev) {
    activity();
    const id = elId(ev.target);
    focusStart[id] = Date.now();
    emit(EVENTS.FOCUS, { el: id });
  }
  function onBlur(ev) {
    const id = elId(ev.target);
    const start = focusStart[id];
    const dwell = start ? Date.now() - start : null;
    emit(EVENTS.BLUR, { el: id, dwell_ms: dwell });
  }

  // ---- input (length only) + retype detection ----
  function onInput(ev) {
    activity();
    const el = ev.target;
    const id = elId(el);
    let len = 0;
    try { len = (el.value || '').length; } catch (_) {}
    emit(EVENTS.INPUT, { el: id, len });

    const field = matchField(el, config.fields);
    const key = field || id;
    if (len > 0) {
      fieldHadContent[key] = true;
    } else if (fieldHadContent[key]) {
      retypes[key] = (retypes[key] || 0) + 1;
      fieldHadContent[key] = false;
      emit(EVENTS.INPUT_CLEARED, { el: id, retypes: retypes[key] });
    }
  }

  // ---- paste (length only) ----
  function onPaste(ev) {
    activity();
    const el = ev.target;
    let len = 0;
    try {
      const data = (ev.clipboardData || window.clipboardData);
      len = data ? (data.getData('text') || '').length : 0;
    } catch (_) {}
    emit(EVENTS.PASTE, { el: elId(el), len });
  }

  // ---- scroll depth ----
  function onScroll() {
    activity();
    try {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const depth = scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 0;
      if (depth > maxScroll) {
        maxScroll = Math.min(100, depth);
        emit(EVENTS.SCROLL, { depth: maxScroll });
      }
    } catch (_) {}
  }

  // ---- idle / hesitation ----
  let idleTimer = null;
  let hbTimer = null;
  let rootObs = null;   // watches the page for late-mounted step/error nodes (SPA support)
  function idleTick() {
    // The idle clock PAUSES while the tab is hidden — being away (reading the SMS,
    // another app) is not abandoning. Keep the clock from ending the session and
    // from counting hidden time as hesitation; visibility-return calls activity().
    try { if (document.visibilityState === 'hidden') { lastActivity = Date.now(); return; } } catch (_) {}
    const gap = Date.now() - lastActivity;
    // Emit a single idle/hesitation event when the gap first crosses the threshold
    // (don't reset lastActivity, so we can track the TOTAL idle below).
    if (gap >= config.idleThresholdMs && !idleEmitted) {
      emit(EVENTS.IDLE, { ms: gap });
      idleEmitted = true;
    }
    // Hard idle timeout: a session VISIBLY inactive this long is abandoned — finalize
    // it (over the live channel) instead of leaving it "Open" until the tab closes.
    // Resurrectable: if the user acts again, activity() reopens the session.
    if (config.idleEndMs && gap >= config.idleEndMs && !ended) {
      end('idle_timeout');
    }
  }

  // ---- tab visibility ----
  let hiddenAt = 0;
  function onVisibility() {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      emit(EVENTS.TAB_HIDDEN, {});
      transport.flush(false);
    } else {
      const away = hiddenAt ? Date.now() - hiddenAt : null;
      activity(); // before emit — resurrects an idle-ended session so tab_visible isn't swallowed
      emit(EVENTS.TAB_VISIBLE, { away_ms: away });
    }
  }

  // ---- step detection (a step container is active when NOT .hidden) ----
  function isVisible(elm) {
    try {
      return elm && !elm.classList.contains('hidden') && isRendered(elm);
    } catch (_) { return false; }
  }
  function detectStep() {
    for (const name in config.steps) {
      const elm = document.querySelector(config.steps[name]);
      if (isVisible(elm)) return name;
    }
    return currentStep;
  }
  function checkStep() {
    const next = detectStep();
    if (next && next !== currentStep) {
      const from = currentStep;
      currentStep = next;
      emit(EVENTS.STEP_CHANGE, { from, to: next });
      if (next === STEPS.SUCCESS) markConverted('success_step');
    }
  }

  // ---- validation errors (error element becomes visible) ----
  function watchErrors(observerFor) {
    for (const field in config.errors) {
      const elm = document.querySelector(config.errors[field]);
      if (!elm) continue;
      observerFor(elm, field);
    }
  }

  // ---- fetch patch: api_call + conversion ----
  function patchFetch() {
    if (!window.fetch || window.fetch.__bb) return;
    const orig = window.fetch;
    const wrapped = function (input, init) {
      const start = Date.now();
      let url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (_) {}
      const method = (init && init.method) || 'GET';
      const p = orig.apply(this, arguments);
      try {
        p.then(function (res) {
          recordApi(url, method, res ? res.status : 0, Date.now() - start, res);
        }).catch(function () {
          recordApi(url, method, 0, Date.now() - start, null);
        });
      } catch (_) {}
      return p;
    };
    wrapped.__bb = true;
    window.fetch = wrapped;
  }

  // The collector endpoint, resolved to a pathname once, so we can ignore the
  // tracer's OWN batch POSTs (transport uses fetch) instead of logging them as
  // page api_call events.
  let ownEndpointPath = null;
  try { ownEndpointPath = new URL(config.endpoint, location.href).pathname; } catch (_) {}

  function recordApi(url, method, status, latency, res) {
    let path = url;
    try { path = new URL(url, location.href).pathname; } catch (_) {}
    if (ownEndpointPath && path === ownEndpointPath) return; // skip self-instrumentation
    // Try to read the error key without consuming the body the page needs.
    let errorKey = null;
    const finish = (key) => emit(EVENTS.API_CALL, {
      url: path, method, status, error_key: key, latency_ms: latency,
    });
    // Only clone+parse the body when we actually need it: an error response (the
    // only place error_key lives) or the configured conversion URL. Cloning EVERY
    // response holds a second copy in memory — a crash risk on heavy prod traffic.
    const isConvUrl = !!(config.conversion.apiSuccessUrl && path.indexOf(config.conversion.apiSuccessUrl) !== -1);
    const wantBody = status >= 400 || isConvUrl;
    try {
      if (wantBody && res && res.clone) {
        res.clone().json().then(function (body) {
          try { errorKey = (body && body.payload && body.payload.user_message) || null; } catch (_) {}
          finish(errorKey);
          // conversion: configured success endpoint returned success
          if (config.conversion.apiSuccessUrl && path.indexOf(config.conversion.apiSuccessUrl) !== -1) {
            try { if (body && body.status === 'success') markConverted('api_success'); } catch (_) {}
          }
        }).catch(function () { finish(null); });
        return;
      }
    } catch (_) {}
    finish(null);
  }

  // ---- js errors ----
  function onError(ev) {
    emit(EVENTS.JS_ERROR, { msg: (ev && ev.message) ? String(ev.message).slice(0, 200) : 'error' });
  }
  function onRejection(ev) {
    let msg = 'unhandledrejection';
    try { msg = String(ev.reason && (ev.reason.message || ev.reason)).slice(0, 200); } catch (_) {}
    emit(EVENTS.JS_ERROR, { msg });
  }

  // ---- conversion + end ----
  function markConverted(reason) {
    if (converted) return;
    converted = true;
    // A conversion means the user reached success — record that as the final step
    // even if the page hadn't navigated there yet (e.g. conversion detected from
    // the confirm API while still on the otp step), so the outcome reads cleanly.
    if (currentStep !== STEPS.SUCCESS) {
      const from = currentStep;
      currentStep = STEPS.SUCCESS;
      emit(EVENTS.STEP_CHANGE, { from, to: STEPS.SUCCESS });
    }
    // Conversion is terminal for a checkout — finalize the session NOW (over the
    // live fetch/beacon) instead of waiting for tab close, so it reports
    // Converted within seconds. Further events are gated once ended.
    end('converted' + (reason ? ':' + reason : ''));
  }

  function end(reason) {
    if (ended) return;
    ended = true;
    const r = reason || 'pagehide';
    endReason = r;
    const duration = Date.now() - t0;
    emit(EVENTS.SESSION_END, {
      converted, last_step: currentStep, duration_ms: duration, reason: r,
    });
    // Beacon ONLY on real unload; alive-path finalizes (converted/idle/cap) go via fetch.
    const unloading = (r === 'pagehide' || r === 'beforeunload');
    transport.flush(true, unloading);
    transport.stop();
    if (idleTimer) { clearInterval(idleTimer); idleTimer = null; }
    if (rootObs) { try { rootObs.disconnect(); } catch (_) {} rootObs = null; }
  }

  // ---- wiring ----
  function start() {
    emitPageLoad();
    emitDomSnapshot();

    document.addEventListener('click', onClick, true);
    document.addEventListener('focus', onFocus, true);
    document.addEventListener('blur', onBlur, true);
    document.addEventListener('input', onInput, true);
    document.addEventListener('paste', onPaste, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    patchFetch();

    // MutationObservers for steps + validation errors.
    try {
      // Observe ONLY class/style attribute changes (a step/error toggles via
      // .hidden), and rAF-debounce the callbacks — never childList/characterData,
      // which fire on every text/DOM change and can pin the CPU on a busy page.
      const stepObs = new MutationObserver(rafDebounce(checkStep));
      // Nodes already wired up. On static pages this is populated once at load and
      // never grows; on SPAs it grows as React mounts each step/error node.
      const bound = new WeakSet();

      // (Re-)resolve the configured step/error selectors against the CURRENT DOM and
      // wire any node we haven't seen yet. Idempotent: a node already in `bound` is
      // skipped, so re-running this never double-observes or double-counts.
      function bindTargets() {
        for (const name in config.steps) {
          const elm = document.querySelector(config.steps[name]);
          if (elm && !bound.has(elm)) {
            bound.add(elm);
            stepObs.observe(elm, { attributes: true, attributeFilter: ['class', 'style'] });
          }
        }
        watchErrors(function (elm, field) {
          if (bound.has(elm)) return;
          bound.add(elm);
          const fire = function () {
            const reason = (elm.getAttribute && elm.getAttribute('data-bb-reason')) || 'shown';
            // The message the user actually saw (page copy, scrubbed of digits/emails)
            // so replay and session detail can show it instead of an empty red box.
            let msg; try { msg = scrubText(elm.textContent || ''); } catch (_) {}
            emit(EVENTS.VALIDATION_ERR, msg ? { field, reason, msg } : { field, reason });
            if (field === 'otp') { otpAttempts++; emit(EVENTS.OTP_ATTEMPT, { n: otpAttempts, result: 'invalid' }); }
          };
          // Static pages: the error node exists at load (hidden) → we catch the
          // hidden→visible toggle below. SPA pages: the node is CREATED already
          // visible on demand → there's no toggle to observe, so emit on first
          // sight if it's already visible at bind time.
          let visiblePrev = isVisible(elm);
          if (visiblePrev) fire();
          const errObs = new MutationObserver(rafDebounce(function () {
            const vis = isVisible(elm);
            if (vis && !visiblePrev) fire();
            visiblePrev = vis;
          }));
          errObs.observe(elm, { attributes: true, attributeFilter: ['class', 'style'] });
        });
        checkStep(); // a freshly-mounted container may already be the active step
      }

      bindTargets();
      currentStep = detectStep();

      // SPA support: step/error nodes on React pages don't exist at load — they're
      // mounted later as the funnel advances. Watch the whole page for node
      // insertions and re-resolve then. rAF-debounced, and on static pages (no node
      // churn) it stays idle, so behavior there is unchanged.
      if (typeof MutationObserver === 'function') {
        const root = document.body || document.documentElement;
        if (root) {
          rootObs = new MutationObserver(rafDebounce(bindTargets));
          rootObs.observe(root, { childList: true, subtree: true });
        }
      }
    } catch (_) {}

    idleTimer = setInterval(idleTick, 2000);

    // Keep-alive: advance the server's last_seen while the visitor is present but
    // idle, so a passive session gets a real dwell time (not 0). Visible tabs only
    // — hidden tabs stay silent (no cost, and the idle clock is paused anyway).
    if (config.heartbeatMs > 0) {
      hbTimer = setInterval(function () {
        try {
          if (endReason) return;
          if (document.visibilityState !== 'visible') return;
          transport.heartbeat();
        } catch (_) {}
      }, config.heartbeatMs);
    }

    // iOS Safari fires pagehide eagerly — for the back-forward cache, app-switch,
    // and some taps, NOT just real exits. Only a true unload (persisted === false)
    // ends the session; a bfcache freeze just flushes and may resume on pageshow.
    // Sessions that never resume get closed by the server-side stale sweep.
    window.addEventListener('pagehide', function (e) {
      if (e && e.persisted) { try { transport.flush(true, true); } catch (_) {} }
      else end('pagehide');
    });
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) activity(); // restored from bfcache — keep the session alive
    });
    window.addEventListener('beforeunload', function () { end('beforeunload'); });

    transport.start();
  }

  // Named milestone from the site's own code (BlackBox.track). Name only.
  function track(name) {
    const n = goalName(name);
    if (n) emit(EVENTS.GOAL, { name: n });
  }

  return { start, markConverted, end, track, getStep: () => currentStep };
}
